import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, CircleNotch, Check, ArrowRight, Warning } from "phosphor-react";
import { Button } from "@/components/ui/button";

export default function DevAdminBootstrap() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState('idle'); // idle, promoting, success, error, unauthorized
  const [errorMessage, setErrorMessage] = useState('');
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      try {
        const response = await api.get('/user/profile');
        setUserProfile(response.data);
        
        // If already admin, show success
        if (response.data.role === 'admin') {
          setStatus('success');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };
    
    if (!authLoading && user) {
      fetchProfile();
    }
  }, [user, authLoading]);

  const handlePromote = async () => {
    setStatus('promoting');
    setErrorMessage('');
    
    try {
      const response = await api.post('/admin/make-admin');
      console.log('Make admin response:', response.data);
      
      // Re-fetch profile to confirm
      const profileResponse = await api.get('/user/profile');
      setUserProfile(profileResponse.data);
      
      if (profileResponse.data.role === 'admin') {
        setStatus('success');
        toast.success('You are now an admin!');
      } else {
        setStatus('error');
        setErrorMessage('Promotion completed but role not updated. Please refresh.');
      }
    } catch (error) {
      console.error('Error promoting to admin:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to promote to admin';
      
      // Check if it's an authorization error
      if (error.response?.status === 403) {
        setStatus('unauthorized');
        setErrorMessage(errorMsg);
      } else {
        setStatus('error');
        setErrorMessage(errorMsg);
      }
      toast.error(errorMsg);
    }
  };

  const goToAdmin = () => {
    navigate('/admin');
  };

  const goToDashboard = () => {
    navigate('/');
  };

  // Show loading while auth is checking
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <CircleNotch size={32} className="animate-spin text-white/40" />
          <p className="text-white/60">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="bg-[#121212] border border-white/10 rounded-sm p-8 max-w-md w-full text-center">
          <ShieldCheck size={48} className="text-yellow-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Not Logged In</h1>
          <p className="text-white/60 mb-6">
            You need to be logged in to access this page.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-white text-black font-bold hover:bg-gray-200"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-white/10 rounded-sm p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck size={32} weight="fill" className="text-white/60" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Admin Bootstrap</h1>
          <p className="text-white/60 text-sm">
            Restricted access - authorized email only
          </p>
        </div>

        {/* User Info */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4 mb-6">
          <div className="text-xs text-white/40 mb-1">Logged in as</div>
          <div className="text-white font-medium">{user.email}</div>
          {userProfile && (
            <div className="mt-2 text-sm">
              <span className="text-white/40">Current role: </span>
              <span className={userProfile.role === 'admin' ? 'text-purple-400 font-bold' : 'text-white/60'}>
                {userProfile.role || 'user'}
              </span>
            </div>
          )}
        </div>

        {/* Status States */}
        {status === 'idle' && (
          <Button
            onClick={handlePromote}
            className="w-full bg-white text-black font-bold hover:bg-gray-200 py-3"
            data-testid="promote-btn"
          >
            <ShieldCheck size={20} className="mr-2" />
            Request Admin Access
          </Button>
        )}

        {status === 'promoting' && (
          <div className="text-center py-4">
            <CircleNotch size={32} className="animate-spin text-white/40 mx-auto mb-3" />
            <p className="text-white/60">Verifying authorization...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4 text-center">
              <Check size={32} className="text-green-400 mx-auto mb-2" />
              <p className="text-green-400 font-bold">You are an admin!</p>
            </div>
            <Button
              onClick={goToAdmin}
              className="w-full bg-purple-500 text-white font-bold hover:bg-purple-400 py-3"
              data-testid="go-to-admin-btn"
            >
              Go to Admin Panel
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        )}

        {status === 'unauthorized' && (
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 text-center">
              <Warning size={32} className="text-red-400 mx-auto mb-2" />
              <p className="text-red-400 font-bold mb-2">Access Denied</p>
              <p className="text-white/60 text-sm">{errorMessage}</p>
            </div>
            <Button
              onClick={goToDashboard}
              variant="outline"
              className="w-full border-white/20 text-white hover:bg-white/5"
            >
              Return to Dashboard
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
              <p className="text-red-400 text-sm">{errorMessage}</p>
            </div>
            <Button
              onClick={handlePromote}
              className="w-full bg-white text-black font-bold hover:bg-gray-200"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Info */}
        <div className="mt-6 pt-4 border-t border-white/10">
          <p className="text-white/30 text-xs text-center">
            Only authorized administrators can access this feature.
          </p>
        </div>
      </div>
    </div>
  );
}
